import { useState, useMemo, useEffect } from 'react';
import { v4 as uuid } from 'uuid';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { isGSFileScanned } from 'hb-react/shared/hooks/useGlobalStorageV2/utils/isGSFileScanned';
import { useSubscribeToAblyChannel } from 'hb-react/shared/hooks/useSubscribeToAblyChannel';
import useVirusScanningEnabled from 'hb-react/shared/hooks/useVirusScanningEnabled';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import { formatFileSize } from 'hb-react/shared/utils/files/fileSize';

import { FILE_TOO_LARGE_ERROR, FILE_UPLOAD_ERROR } from '../constants';
import type {
  GlobalStorageFile,
  GetAddFiles,
  GetRemoveFiles,
  GetUpdateStoredFiles,
  UseGlobalStorageFiles,
} from '../types';
import { OwnerType } from '../types';
import {
  fileMatchesIdentifiers,
  getAblyVirusScanChannel,
  getAblyTokenType,
  presignAndUpload,
  getAblyVirusScanMessageHandler,
} from '../utils';

const getUpdateStoredFile: GetUpdateStoredFiles =
  ({ setStoredFiles }) =>
  (identifiers, fileOrFunc) =>
    setStoredFiles((prevFiles) => {
      const fileToUpdate = prevFiles.find((prevFile) =>
        fileMatchesIdentifiers(prevFile, identifiers),
      );

      if (!fileToUpdate) {
        return prevFiles;
      }

      // The file is already in an error state, maintain its state to make debugging easier
      if (fileToUpdate.error) {
        return prevFiles;
      }

      return prevFiles.map((prevFile) => {
        if (prevFile === fileToUpdate) {
          const newFile =
            typeof fileOrFunc === 'function'
              ? fileOrFunc(prevFile)
              : { ...prevFile, ...fileOrFunc };

          return newFile;
        }

        return prevFile;
      });
    });

const getAddFiles: GetAddFiles =
  ({
    currentNetworkId,
    markFileAsRemovedOnError,
    maxFileSize,
    ownerType,
    setStoredFiles,
    subscribeToAblyChannel,
    updateStoredFile,
    virusScanningEnabled,
  }) =>
  async (filesData) => {
    const newFiles: WithRequired<GlobalStorageFile, 'file'>[] = filesData
      .filter(Boolean) // Necessary or hiding bugs?
      .map(({ file, key, onProgress }) => ({
        file,
        key: key ?? uuid(),
        uploadPercentage: 0,
        abortController: new AbortController(),
        onProgress,
      }));

    setStoredFiles((prevFiles) => [...prevFiles, ...newFiles]);

    if (virusScanningEnabled) {
      await subscribeToAblyChannel();
    }

    return Promise.allSettled(
      newFiles.map(
        async ({
          file,
          key,
          onProgress,
          abortController: controller,
        }): Promise<string | null> => {
          const isFileTooLarge = maxFileSize && file.size > maxFileSize;
          const fileTooLargeError = isFileTooLarge
            ? FILE_TOO_LARGE_ERROR.i__({
                size: formatFileSize(maxFileSize),
              })
            : undefined;

          if (fileTooLargeError) {
            updateStoredFile(
              { key },
              {
                error: fileTooLargeError,
                isRemoved: markFileAsRemovedOnError,
              },
            );

            return Promise.reject(new Error(fileTooLargeError));
          }

          try {
            return await presignAndUpload({
              controller,
              file,
              inBO: ownerType === OwnerType.ADMIN,
              key,
              networkId: currentNetworkId,
              onProgress,
              updateStoredFile,
              virusScanningEnabled,
            });
          } catch (error) {
            errorReporting.captureException(error, {
              tags: { tribe: 'evolution-tribe' },
            });

            const errorMessage = FILE_UPLOAD_ERROR.i__({
              name: file?.name,
            });

            updateStoredFile(
              { key },
              {
                error: errorMessage,
                isRemoved: markFileAsRemovedOnError,
              },
            );

            return Promise.reject(new Error(errorMessage));
          }
        },
      ),
    );
  };

const getRemoveFiles: GetRemoveFiles =
  ({ setStoredFiles }) =>
  ({ files }) =>
    setStoredFiles((prevFiles) =>
      prevFiles.map((prevFile) => {
        const mustRemove = files.some(({ key }) => key === prevFile.key);

        if (mustRemove) {
          // Cancel any pending request associated to this file
          prevFile.abortController.abort();

          return {
            ...prevFile,
            isRemoved: true,
          };
        }

        return prevFile;
      }),
    );

export const useGlobalStorageFiles: UseGlobalStorageFiles = ({
  initialFiles = [],
  config,
}) => {
  const { currentAdmin, currentUser, currentNetworkId } =
    useDefaultAppContexts();
  const virusScanningEnabled = useVirusScanningEnabled();

  const [storedFiles, setStoredFiles] =
    useState<GlobalStorageFile[]>(initialFiles);

  const updateStoredFile = useMemo(
    () => getUpdateStoredFile({ setStoredFiles }),
    [],
  );

  const ablyVirusScanMessageHandler = useMemo(
    () =>
      getAblyVirusScanMessageHandler(
        updateStoredFile,
        config?.markFileAsRemovedOnError,
      ),
    [config?.markFileAsRemovedOnError, updateStoredFile],
  );

  const ownerType = currentAdmin ? OwnerType.ADMIN : OwnerType.USER;
  const ownerId = currentAdmin?.id ?? currentUser?.id;

  const { subscribe, unsubscribe } = useSubscribeToAblyChannel({
    autoConnect: false,
    channel: getAblyVirusScanChannel(ownerType, ownerId),
    enabled: virusScanningEnabled,
    onMessage: ablyVirusScanMessageHandler,
    onResubscribedAfterInactivity: null,
    tokenType: getAblyTokenType(ownerType),
  });

  const addFiles = useMemo(
    () =>
      getAddFiles({
        setStoredFiles,
        updateStoredFile,
        virusScanningEnabled,
        ownerType,
        currentNetworkId,
        maxFileSize: config?.maxFileSize,
        markFileAsRemovedOnError: config?.markFileAsRemovedOnError,
        subscribeToAblyChannel: subscribe,
      }),
    [
      config?.markFileAsRemovedOnError,
      config?.maxFileSize,
      currentNetworkId,
      ownerType,
      subscribe,
      updateStoredFile,
      virusScanningEnabled,
    ],
  );

  const removeFiles = useMemo(() => getRemoveFiles({ setStoredFiles }), []);

  useEffect(() => {
    if (storedFiles.length === 0) {
      return;
    }

    // Unsubscribe from the ably channel once the file has been scanned or errored out
    if (
      storedFiles.every(
        (storedFile) => isGSFileScanned(storedFile) || storedFile.error,
      )
    ) {
      unsubscribe();
    }
  }, [storedFiles, unsubscribe]);

  return {
    files: storedFiles,
    setFiles: setStoredFiles,
    addFiles,
    removeFiles,
  };
};
